
import React, { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Trash2, Upload, Image as ImageIcon, Pencil } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface ImageUploaderProps {
  imageUrl: string | null;
  onImageUploaded: (url: string) => void;
  onImageRemoved?: () => void;
  folder?: string;
  className?: string;
  aspectRatio?: 'square' | 'banner' | 'portrait';
  buttonText?: string;
  showRemoveButton?: boolean;
  editable?: boolean;
  editMode?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  imageUrl,
  onImageUploaded,
  onImageRemoved,
  folder = 'default',
  className,
  aspectRatio = 'square',
  buttonText = 'Upload Image',
  showRemoveButton = true,
  editable = false,
  editMode = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const aspectRatioClass = {
    square: 'aspect-square',
    banner: 'aspect-[3/1]',
    portrait: 'aspect-[3/4]'
  };

  const handleImageClick = () => {
    if (editMode || editable) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB',
        variant: 'destructive'
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, JPG, JPEG, GIF)',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);

    try {
      // Get user ID for folder structure
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      const userId = user.id;
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${userId}/${folder}/${fileName}`;

      const { error: uploadError, data } = await supabase.storage
        .from('business_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('business_images')
        .getPublicUrl(filePath);

      onImageUploaded(publicUrl);
      
      toast({
        title: 'Image uploaded',
        description: 'Your image was uploaded successfully',
      });
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'There was a problem uploading your image',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async () => {
    if (!imageUrl || !onImageRemoved) return;
    
    setLoading(true);
    
    try {
      // Extract the path from the URL
      const urlObj = new URL(imageUrl);
      const pathWithQuery = urlObj.pathname;
      const pathSegments = pathWithQuery.split('/');
      const bucketName = pathSegments[1];
      const filePath = pathSegments.slice(2).join('/');
      
      if (bucketName === 'business_images') {
        await supabase.storage
          .from('business_images')
          .remove([filePath]);
      }
      
      onImageRemoved();
      
      toast({
        title: 'Image removed',
        description: 'Your image was removed successfully',
      });
    } catch (error: any) {
      console.error('Error removing image:', error);
      toast({
        title: 'Error',
        description: 'There was a problem removing your image',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn('relative', className)}>
      <div 
        className={cn(
          'border border-dashed rounded-lg overflow-hidden flex items-center justify-center bg-muted/30',
          aspectRatioClass[aspectRatio],
          imageUrl ? 'border-transparent' : 'border-muted-foreground/30'
        )}
        onClick={handleImageClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {imageUrl ? (
          <div className="w-full h-full relative group">
            <img 
              src={imageUrl} 
              alt="Uploaded" 
              className="w-full h-full object-cover transition-opacity" 
            />
            {(editMode || (editable && isHovered)) && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity">
                <Button variant="secondary" size="sm" disabled={uploading} className="mx-auto">
                  <Upload className="h-4 w-4 mr-2" />
                  Change
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <ImageIcon className="h-10 w-10 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">{buttonText}</p>
            {editable && !editMode && (
              <Button variant="secondary" size="sm" className="mt-2">
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            )}
          </div>
        )}
        
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
            <Skeleton className={cn('h-full w-full', aspectRatioClass[aspectRatio])} />
          </div>
        )}
      </div>
      
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
        disabled={uploading || loading}
      />
      
      {/* Edit button overlay for when image exists */}
      {imageUrl && editable && !editMode && (
        <Button 
          variant="secondary" 
          size="sm"
          className={cn(
            "absolute top-2 right-2 h-8 w-8 rounded-full p-0 opacity-0 transition-opacity",
            isHovered ? "opacity-100" : "opacity-0"
          )}
          onClick={handleImageClick}
          disabled={uploading || loading}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      )}
      
      {/* Remove button (visible in edit mode or when hovered if editable) */}
      {imageUrl && showRemoveButton && (editMode || (editable && isHovered)) && (
        <Button 
          variant="destructive" 
          size="sm"
          className="absolute -top-2 -right-2 h-8 w-8 rounded-full p-0"
          onClick={handleRemoveImage}
          disabled={uploading || loading}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default ImageUploader;
